import React, { useState, useEffect } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNotification, NOTIFICATION_TYPES } from './NotificationContext';
import { apiPost } from '../utils/api';
import { notificationService } from '../services/notificationService';
import { getFullChain } from '../services/userSelf';
import useTranslation from '../services/translationService';
import { isBreakglassModeActive, isUserRestricted } from '../services/breakglassService';

// Define USER_TYPES to match backend
const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

// Helper function to get hierarchy level
const getHierarchyLevel = (type) => {
  const levels = {
    'admin': 6,
    'country': 5,
    'city': 4,
    'super-agent': 3,
    'agent': 2,
    'player': 1
  };
  return levels[type] || 0;
};

export default function ChangePasswordModal({ isOpen, onClose, username, userType }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isBreakglassActive, setIsBreakglassActive] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  // Check for breakglass mode and restricted users
  useEffect(() => {
    const checkRestrictions = async () => {
      const breakglassActive = await isBreakglassModeActive();
      const restricted = await isUserRestricted();
      setIsBreakglassActive(breakglassActive);
      setIsRestricted(restricted);
    };

    if (isOpen) {
      checkRestrictions();
    }
  }, [isOpen]);

  // Log props when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('ChangePasswordModal opened with:', { username, userType });
    }
  }, [isOpen, username, userType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isLoading) {
      console.log('Already submitting, ignoring duplicate form submission');
      return;
    }
    
    // Check breakglass mode and restricted users
    if (isBreakglassActive || isRestricted) {
      setError(t('auth.errors.breakglassMode', 'Password changes are disabled for security reasons'));
      return;
    }
    
    setError('');
    setIsLoading(true);

    // Basic validation
    if (!password) {
      setError(t('auth.errors.passwordRequired', 'Please enter a password'));
      setIsLoading(false);
      return;
    }
    
    // Password validation
    if (password.length < 6) {
      setError(t('auth.errors.passwordLength', 'Password must be at least 6 characters long'));
      setIsLoading(false);
      return;
    }

    // Ensure username is valid
    if (!username) {
      setError(t('auth.errors.usernameRequired', 'Username is required'));
      setIsLoading(false);
      return;
    }

    // Ensure userType is valid
    if (!userType || !Object.values(USER_TYPES).includes(userType)) {
      console.error('Invalid user type:', userType);
      setError(t('auth.errors.invalidUserType', `Invalid user type: ${userType}`));
      setIsLoading(false);
      return;
    }

    const passwordData = {
      action: 'changePassword',
      username,
      password
    };

    console.log('ChangePasswordModal: submitting password change', { username, type: userType });

    try {
      // Get the authentication token
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken) {
        throw new Error(t('auth.errors.loginAgain', 'Please log in again'));
      }
      
      // Make the API call using apiPost
      const response = await apiPost(
        'coreAgent',
        '/core/agent',
        passwordData,
        tokens.idToken.toString()
      );
      
      console.log('Password change response:', response);
      
      if (response.success) {
        showNotification(
          t('auth.success.passwordChanged', `Successfully changed password for ${username}`),
          NOTIFICATION_TYPES.SUCCESS
        );

        try {
          // Get the current user's username from the auth session
          const currentUsername = tokens.accessToken.payload.username;
          
          // Get the full chain for the target user
          const recipients = await getFullChain(username);
          
          console.log('Recipients chain:', recipients);
          
          if (recipients.length > 0) {
            // Send notification to the user and their entire upward chain
            await notificationService.sendNotification({
              content: t('auth.notifications.passwordChanged', { changer: currentUsername, target: username }),
              recipients
            });
            
            console.log('Password change notifications sent to user and agent chain:', recipients);
          }
        } catch (notifyError) {
          console.error('Failed to send password change notifications:', notifyError);
          // Don't block the UI for notification failures
        }

        onClose();
      } else {
        // Handle specific error cases from the backend
        switch (response.error) {
          case 'Target user not found':
            setError(t('auth.errors.userNotFound', `User "${username}" does not exist in the system`));
            break;
          case 'Insufficient permissions to change password':
            setError(t('auth.errors.insufficientPermissions', 'You do not have permission to change passwords'));
            break;
          case 'Insufficient permissions to change this user\'s password':
            setError(t('auth.errors.insufficientPermissionsForUser', 'You do not have permission to change this user\'s password'));
            break;
          case 'Missing required fields for password change':
            setError(t('auth.errors.missingFields', 'Missing required information for password change'));
            break;
          default:
            // Handle any other error messages from the backend
            setError(response.message || response.error || t('auth.errors.changePasswordFailed', 'Failed to change password'));
        }
      }
    } catch (error) {
      console.error('Error changing password:', error);
      // Handle network or other unexpected errors
      if (error.message.includes('Network Error')) {
        setError(t('common.errors.network', 'Network error: Please check your internet connection'));
      } else if (error.message.includes('Token')) {
        setError(t('auth.errors.sessionExpired', 'Your session has expired. Please log in again'));
      } else {
        setError(t('auth.errors.unexpected', 'An unexpected error occurred while changing the password'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <KeyIcon className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('auth.changePassword')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {(isBreakglassActive || isRestricted) && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('auth.breakglassMode', 'Breakglass Mode Active')}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    {t('auth.breakglassModeDescription', 'Password changes are temporarily disabled for security reasons.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('auth.username')}
              </label>
              <input
                type="text"
                id="username"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                value={username}
                disabled
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('auth.newPassword')}
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.enterNewPassword', 'Enter new password')}
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  <button
                    type="button"
                    className="px-3 py-2 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/50 text-red-500 dark:text-red-200 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || isBreakglassActive || isRestricted}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('auth.changing', 'Changing...') : t('auth.changePassword')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 